import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeftIcon,
  CheckIcon,
  CircleHelpIcon,
  CoinsIcon,
  CrownIcon,
  DumbbellIcon,
  HandHeartIcon,
  LockIcon,
  PaletteIcon,
  PawPrintIcon,
  SparklesIcon,
  ShoppingBagIcon,
  SwordIcon,
  TrophyIcon,
  UtensilsIcon,
  XIcon,
  type LucideIcon,
} from "lucide-react";

import { Column } from "../Column";
import { Row } from "../Row";
import { Text } from "../Text";
import { ShopAdjustControls } from "./shop/ShopAdjustControls";
import { ShopCatalogGrid } from "./shop/ShopCatalogGrid";
import { ShopCharacterSheet } from "./shop/ShopCharacterSheet";
import { ShopCrownToggle } from "./shop/ShopCrownToggle";
import { shopCatalog } from "./shop/shopCatalog";
import { ShopModeTabs } from "./shop/ShopModeTabs";
import { ShopOutfitChanges } from "./shop/ShopOutfitChanges";
import type {
  ShopItemFilter,
  ShopMode,
  ShopOutfit,
  ShopSlot,
} from "./shop/types";
import { StafferPetIncentiveToken } from "./StafferPetIncentiveToken";
import { StafferPetProfileSprite } from "./StafferPetProfileSprite";
import { StafferPetSprite } from "./StafferPetSprite";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import {
  STAFFER_PET_REWARD_DETAILS,
  STAFFER_PET_SEARCH_SUBMIT_REWARD,
  useStafferPet,
} from "../../hooks/useStafferPets";
import { useGetPetTransactions } from "@/hooks/queries/useGetPetTransactions";
import { usePurchasePetItem } from "@/hooks/updateSets/usePurchasePetItem";
import { useRedeemIncentive } from "@/hooks/updateSets/useRedeemIncentive";
import { useUpsertPet } from "@/hooks/updateSets/useUpsertPet";
import { dates } from "../../lib/dates";
import { stafferPetShop } from "../../lib/stafferPetShop";
import { stafferPets } from "../../lib/stafferPets";
import { cn } from "../../lib/utils";
import type {
  Pet,
  StafferPetSpecies,
  StafferPetTopperOffset,
} from "../../types";

const PET_PREVIEW_SIZE_PX = 184;
const STANDALONE_PREVIEW_SIZE_PX = 224;
const UNLOCK_CELEBRATION_DURATION_MS = 1_500;
const ACCESSORY_ROTATION_STEP_DEG = 45;
export type ShopCategory = (typeof stafferPetShop.categoryIds)[number];
type CustomCommission = (typeof stafferPetShop.sections.CUSTOM.items)[number];
type CustomCommissionId = CustomCommission["id"];

const customCommissionOptions = stafferPetShop.sections.CUSTOM.items;
const shopModes: ShopMode[] = ["CUSTOMIZE", "CUSTOM", "INCENTIVES"];
// Deep links into the old per-category tabs land on the matching slot.
const categorySlots: Partial<Record<ShopCategory, ShopSlot>> = {
  PETS: "PET",
  COLORS: "COLOR",
  TOPPERS: "TOPPER",
  ACCESSORIES: "ACCESSORY_1",
};
const slotLabels: Record<ShopSlot, string> = {
  PET: "Pet",
  COLOR: "Color",
  TOPPER: "Head",
  ACCESSORY_1: "Accessory 1",
  ACCESSORY_2: "Accessory 2",
};

const movePurchasedItemsToBottom = <Item,>(
  items: readonly Item[],
  isPurchased: (item: Item) => boolean,
): Item[] => [
  ...items.filter((item) => !isPurchased(item)),
  ...items.filter(isPurchased),
];

const withUnlock = <Id,>(
  ids: readonly Id[],
  id: Id | null,
  isUnlocking: boolean,
): Id[] => (isUnlocking && id !== null ? [...new Set([...ids, id])] : [...ids]);

export const StafferPetShopWizard = ({
  initialCategory = null,
  open,
  onBack,
  onOpenChange,
}: {
  // Tab to land on when the dialog opens; null keeps the last-viewed tab.
  initialCategory?: ShopCategory | null;
  open: boolean;
  onBack: () => void;
  onOpenChange: (open: boolean) => void;
}) => {
  const { coinBalance, currentUser, pet: currentUserPet } = useStafferPet();

  /* Database CRUD Operations */
  // Update
  // Equipping something already owned is a cosmetic update; spending coins
  // goes through the purchase endpoint, where guac moves the coins atomically.
  const { mutate: upsertPet, isPending: isEquipping } = useUpsertPet();
  const { mutate: purchasePetItem, isPending: isSpending } =
    usePurchasePetItem();
  const isPurchasing = isEquipping || isSpending;

  /* React Hooks */
  const [mode, setMode] = useState<ShopMode>("CUSTOMIZE");
  // Toppers first: their tiles are plain images, so the shop opens without
  // recoloring every species. Pet tiles render when that tab is picked.
  const [activeSlot, setActiveSlot] = useState<ShopSlot>("TOPPER");
  const [itemFilter, setItemFilter] = useState<ShopItemFilter>("ALL");
  // The outfit being built across slots; null until seeded from the pet.
  const [outfit, setOutfit] = useState<ShopOutfit | null>(null);
  const [isEvolvedPreview, setIsEvolvedPreview] = useState(false);
  // Spending coins takes a second click, since one save can unlock several items.
  const [isConfirmingPurchase, setIsConfirmingPurchase] = useState(false);
  const [topperOffsets, setTopperOffsets] = useState<
    Record<string, StafferPetTopperOffset>
  >({});
  const [dirtyTopperOffsetKeys, setDirtyTopperOffsetKeys] = useState<
    Set<string>
  >(new Set());
  const [accessoryAdjustments, setAccessoryAdjustments] = useState<
    Record<string, StafferPetTopperOffset>
  >({});
  const [dirtyAccessoryAdjustmentKeys, setDirtyAccessoryAdjustmentKeys] =
    useState<Set<string>>(new Set());
  // Anyone can try the crown on to pre-set its position; it's never equipped.
  const [isCrownPreview, setIsCrownPreview] = useState(false);
  const [isCrownLeaderboardOnly, setIsCrownLeaderboardOnly] = useState(false);
  const [selectedCustomId, setSelectedCustomId] = useState<CustomCommissionId>(
    customCommissionOptions[0].id,
  );
  // Commissions are buyable again and again, so "purchased" only lasts for
  // this visit — closing the wizard resets the cards to purchaseable.
  const [purchasedCustomIds, setPurchasedCustomIds] = useState<
    Set<CustomCommissionId>
  >(new Set());
  const [celebrationMessage, setCelebrationMessage] = useState<string | null>(
    null,
  );
  const seededRef = useRef(false);
  const collectionStats = useMemo(
    () =>
      currentUserPet === null
        ? null
        : shopCatalog.getCollectionStats(currentUserPet),
    [currentUserPet],
  );

  useEffect(() => {
    if (!open || initialCategory === null) return;
    const slot = categorySlots[initialCategory];
    if (slot) {
      setMode("CUSTOMIZE");
      setActiveSlot(slot);
      return;
    }
    if (initialCategory === "CUSTOM" || initialCategory === "INCENTIVES") {
      setMode(initialCategory);
    }
  }, [initialCategory, open]);

  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      setCelebrationMessage(null);
      setPurchasedCustomIds(new Set());
      setIsEvolvedPreview(false);
      setIsConfirmingPurchase(false);
      setIsCrownPreview(false);
      setOutfit(null);
      return;
    }
    if (seededRef.current || currentUserPet === null) return;

    // Open on what the pet is wearing, not on something to buy.
    setOutfit(shopCatalog.getOutfit(currentUserPet));
    setIsEvolvedPreview(currentUserPet.isEvolved ?? false);
    setItemFilter("ALL");
    setTopperOffsets(currentUserPet.topperOffsets ?? {});
    setDirtyTopperOffsetKeys(new Set());
    setAccessoryAdjustments(currentUserPet.accessoryAdjustments ?? {});
    setDirtyAccessoryAdjustmentKeys(new Set());
    // Crowned pets open wearing it, since that's how everyone sees them.
    setIsCrownPreview(currentUserPet.isCrowned ?? false);
    setIsCrownLeaderboardOnly(currentUserPet.isCrownLeaderboardOnly ?? false);
    seededRef.current = true;
  }, [currentUserPet, open]);

  useEffect(() => {
    if (celebrationMessage === null) return;

    const celebrationTimeout = window.setTimeout(() => {
      setCelebrationMessage(null);
    }, UNLOCK_CELEBRATION_DURATION_MS);

    return () => {
      window.clearTimeout(celebrationTimeout);
    };
  }, [celebrationMessage]);

  const draft =
    outfit ??
    (currentUserPet === null ? null : shopCatalog.getOutfit(currentUserPet));
  const previewSpecies: StafferPetSpecies = draft?.petId ?? "FOX";
  const topperId = draft?.topperId ?? null;
  const accessoryId = draft?.accessoryId ?? null;
  const accessoryId2 = draft?.accessoryId2 ?? null;
  const isAccessorySlot =
    activeSlot === "ACCESSORY_1" || activeSlot === "ACCESSORY_2";
  const activeAccessorySlot: 1 | 2 = activeSlot === "ACCESSORY_2" ? 2 : 1;
  const activeAccessoryId =
    activeAccessorySlot === 1 ? accessoryId : accessoryId2;
  const evolutionId = stafferPets.getEvolutionId(previewSpecies);
  // Evolutions are preview-only for now: anyone can peek, nothing unlocks.
  const isPreviewEvolved = evolutionId !== null && isEvolvedPreview;
  const previewTopperId = isCrownPreview
    ? stafferPets.CROWN_TOPPER_ID
    : topperId;
  const selectedTopperOffsetKey =
    previewTopperId === null
      ? null
      : stafferPets.getTopperOffsetKey(
          previewSpecies,
          previewTopperId,
          isPreviewEvolved,
        );
  const previewTopperOffset = stafferPets.getTopperOffset(
    topperOffsets,
    previewSpecies,
    previewTopperId,
    isPreviewEvolved,
  );
  const previewAccessoryAdjustment = stafferPets.getAccessoryAdjustment(
    accessoryAdjustments,
    previewSpecies,
    accessoryId,
    isPreviewEvolved,
  );
  const previewAccessoryAdjustment2 = stafferPets.getAccessoryAdjustment(
    accessoryAdjustments,
    previewSpecies,
    accessoryId2,
    isPreviewEvolved,
  );
  const activeAccessoryAdjustment =
    activeAccessorySlot === 1
      ? previewAccessoryAdjustment
      : previewAccessoryAdjustment2;
  const cartItems =
    currentUserPet === null || draft === null
      ? []
      : shopCatalog.getCartItems(currentUserPet, draft);
  const cartCost = cartItems.reduce((sum, item) => sum + item.price, 0);
  const changedSlots =
    currentUserPet === null || draft === null
      ? []
      : shopCatalog.getChangedSlots(currentUserPet, draft);
  const isPositionDirty =
    dirtyTopperOffsetKeys.size > 0 || dirtyAccessoryAdjustmentKeys.size > 0;
  const isCrownSettingDirty =
    isCrownLeaderboardOnly !==
    (currentUserPet?.isCrownLeaderboardOnly ?? false);
  const hasOutfitChanges =
    changedSlots.length > 0 || isPositionDirty || isCrownSettingDirty;
  const canSaveOutfit =
    currentUser !== null &&
    currentUserPet !== null &&
    hasOutfitChanges &&
    (cartCost === 0 || stafferPetShop.isPurchasingEnabled) &&
    cartCost <= coinBalance &&
    !isPurchasing;
  const catalogEntries =
    currentUserPet === null || draft === null
      ? []
      : shopCatalog.getEntries(activeSlot, currentUserPet, draft);

  const selectedCustom =
    customCommissionOptions.find((option) => option.id === selectedCustomId) ??
    customCommissionOptions[0];
  const isSelectedCustomOwned = purchasedCustomIds.has(selectedCustom.id);
  const canPurchaseCustom =
    stafferPetShop.isPurchasingEnabled &&
    currentUser !== null &&
    currentUserPet !== null &&
    !isSelectedCustomOwned &&
    coinBalance >= selectedCustom.price &&
    !isPurchasing;
  const canSubmitByMode: Record<ShopMode, boolean> = {
    CUSTOMIZE: canSaveOutfit,
    CUSTOM: canPurchaseCustom,
    INCENTIVES: false,
  };

  /* Helpers */
  const getSheetSlot = (slot: ShopSlot) => ({
    cost: cartItems.find((item) => item.slot === slot)?.price ?? 0,
    isChanged: changedSlots.includes(slot),
    item: draft === null ? null : shopCatalog.getSlotItem(draft, slot),
  });

  const getOutfitChanges = () =>
    changedSlots.map((slot) => ({
      key: slot,
      label: `${slotLabels[slot]} → ${getSheetSlot(slot).item?.label ?? "None"}`,
      price: cartItems.find((item) => item.slot === slot)?.price ?? 0,
    }));

  // Short form of the footer CTA, sized for the character sheet's button row.
  const renderSheetSaveLabel = () => {
    if (isPurchasing) return "Saving…";
    if (cartCost > 0 && isConfirmingPurchase) return "Confirm";
    if (cartCost > 0) return "Unlock & save";
    return "Save";
  };

  const renderEvolutionTooltip = () => {
    if (isEvolvedPreview) return "Show normal form";
    return "Preview evolution";
  };

  const renderSubmitLabel = () => {
    if (mode === "INCENTIVES") return "REDEEM WITH TOKENS ABOVE";
    if (currentUserPet === null) return "ADOPT A PET FIRST";
    if (isPurchasing) return "SAVING…";
    if (mode === "CUSTOM") {
      if (!stafferPetShop.isPurchasingEnabled) return "PRICES COMING SOON";
      if (isSelectedCustomOwned) return "PURCHASED · NOTIFY TYLER";
      if (coinBalance < selectedCustom.price) return "NOT ENOUGH COINS";
      return `UNLOCK FOR ${selectedCustom.price.toString()} COINS`;
    }
    if (!hasOutfitChanges) return "WEARING THIS OUTFIT";
    if (cartCost > 0 && !stafferPetShop.isPurchasingEnabled) {
      return "PRICES COMING SOON";
    }
    if (cartCost > coinBalance) {
      return `NEED ${(cartCost - coinBalance).toString()} MORE COINS`;
    }
    if (cartCost > 0 && isConfirmingPurchase) {
      return `CONFIRM · SPEND ${cartCost.toString()} COINS`;
    }
    if (cartCost > 0) {
      return `UNLOCK ${cartItems.length.toString()} · ${cartCost.toString()} COINS & SAVE`;
    }
    return "SAVE OUTFIT";
  };

  const renderStandaloneLabel = () => {
    if (mode === "INCENTIVES") return "Incentive Rewards";
    return selectedCustom.title;
  };

  const renderStandaloneMeta = () => {
    if (mode === "INCENTIVES") {
      const tokens = currentUserPet?.incentiveTokensAvailable ?? 0;
      return `${tokens.toString()} incentive token${tokens === 1 ? "" : "s"} to spend`;
    }
    if (isSelectedCustomOwned) return "Notify Tyler to get started";
    return "Made just for you";
  };

  /* Handlers */
  const handleModeSelect = (nextMode: ShopMode) => {
    setCelebrationMessage(null);
    setIsConfirmingPurchase(false);
    setMode(nextMode);
  };

  const handleOutfitChange = (nextOutfit: ShopOutfit) => {
    setCelebrationMessage(null);
    setIsConfirmingPurchase(false);
    // A pet swap shows the new pet in the pet's real (leaderboard) state.
    if (nextOutfit.petId !== draft?.petId) {
      setIsEvolvedPreview(currentUserPet?.isEvolved ?? false);
    }
    setOutfit(nextOutfit);
  };

  const handleItemSelect = (id: string) => {
    if (draft === null) return;
    // Picking a topper means they want to see it, not the crown over it.
    if (activeSlot === "TOPPER") setIsCrownPreview(false);
    handleOutfitChange(shopCatalog.applyItem(draft, activeSlot, id));
  };

  const handleSlotClear = (slot: ShopSlot) => {
    if (draft === null) return;
    handleOutfitChange(shopCatalog.applyItem(draft, slot, null));
  };

  const handleShuffle = () => {
    if (currentUserPet === null || draft === null) return;
    handleOutfitChange(shopCatalog.getShuffledOutfit(currentUserPet, draft));
  };

  const handleReset = () => {
    if (currentUserPet === null) return;
    handleOutfitChange(shopCatalog.getOutfit(currentUserPet));
    setTopperOffsets(currentUserPet.topperOffsets ?? {});
    setDirtyTopperOffsetKeys(new Set());
    setAccessoryAdjustments(currentUserPet.accessoryAdjustments ?? {});
    setDirtyAccessoryAdjustmentKeys(new Set());
    setIsCrownLeaderboardOnly(currentUserPet.isCrownLeaderboardOnly ?? false);
  };

  const handleTopperOffsetChange = (offset: StafferPetTopperOffset) => {
    if (selectedTopperOffsetKey === null) return;
    setTopperOffsets((current) => ({
      ...current,
      [selectedTopperOffsetKey]: offset,
    }));
    setDirtyTopperOffsetKeys((current) =>
      new Set(current).add(selectedTopperOffsetKey),
    );
  };

  const handleTopperScaleChange = (amount: number) => {
    handleTopperOffsetChange(
      stafferPets.clampTopperOffset(
        {
          ...previewTopperOffset,
          scale: (previewTopperOffset.scale ?? 1) + amount,
        },
        previewTopperId,
      ),
    );
  };

  const handleAccessoryDragStart = (slot: 1 | 2) => {
    setActiveSlot(slot === 1 ? "ACCESSORY_1" : "ACCESSORY_2");
  };

  const handleAccessoryAdjustmentChange = (
    slot: 1 | 2,
    adjustment: StafferPetTopperOffset,
  ) => {
    const id = slot === 1 ? accessoryId : accessoryId2;
    if (id === null) return;
    const key = stafferPets.getAccessoryAdjustmentKey(
      previewSpecies,
      id,
      isPreviewEvolved,
    );
    setAccessoryAdjustments((current) => ({
      ...current,
      [key]: stafferPets.clampAccessoryAdjustment(adjustment, id),
    }));
    setDirtyAccessoryAdjustmentKeys((current) => new Set(current).add(key));
  };

  const handleAccessoryScaleChange = (amount: number) => {
    handleAccessoryAdjustmentChange(
      activeAccessorySlot,
      stafferPets.clampAccessoryAdjustment(
        {
          ...activeAccessoryAdjustment,
          scale: (activeAccessoryAdjustment.scale ?? 1) + amount,
        },
        activeAccessoryId,
      ),
    );
  };

  const handleAccessoryRotate = () => {
    handleAccessoryAdjustmentChange(
      activeAccessorySlot,
      stafferPets.clampAccessoryAdjustment(
        {
          ...activeAccessoryAdjustment,
          rotation:
            (activeAccessoryAdjustment.rotation ?? 0) +
            ACCESSORY_ROTATION_STEP_DEG,
        },
        activeAccessoryId,
      ),
    );
  };

  const handleCustomSelect = (customId: CustomCommissionId) => {
    setCelebrationMessage(null);
    setSelectedCustomId(customId);
  };

  const handleSaveOutfit = () => {
    // canSaveOutfit already narrows currentUser and currentUserPet.
    if (!canSaveOutfit || draft === null) return;
    if (cartCost > 0 && !isConfirmingPurchase) {
      setIsConfirmingPurchase(true);
      return;
    }

    // The pet payload carries only cosmetic state (equips, unlocks, offsets);
    // guac owns the ledger, so unowned items also send one combined spend.
    const cartSlots = new Set(cartItems.map((item) => item.slot));
    const nextPet: Pet = {
      ...currentUserPet,
      equipped: { ...currentUserPet.equipped, ...draft },
      unlocked: {
        ...currentUserPet.unlocked,
        petIds: withUnlock(
          currentUserPet.unlocked.petIds,
          draft.petId,
          cartSlots.has("PET"),
        ),
        colorIds: withUnlock(
          currentUserPet.unlocked.colorIds,
          draft.colorId,
          cartSlots.has("COLOR"),
        ),
        topperIds: withUnlock(
          currentUserPet.unlocked.topperIds,
          draft.topperId,
          cartSlots.has("TOPPER"),
        ),
        accessoryIds: withUnlock(
          withUnlock(
            currentUserPet.unlocked.accessoryIds ?? [],
            draft.accessoryId,
            cartSlots.has("ACCESSORY_1"),
          ),
          draft.accessoryId2,
          cartSlots.has("ACCESSORY_2"),
        ),
      },
      topperOffsets,
      accessoryAdjustments,
      isCrownLeaderboardOnly,
    };
    const callbacks = {
      onSuccess: () => {
        setDirtyTopperOffsetKeys(new Set());
        setDirtyAccessoryAdjustmentKeys(new Set());
        setCelebrationMessage(
          cartItems.length > 0
            ? `${cartItems.length.toString()} unlocked!`
            : "Outfit saved!",
        );
      },
      onSettled: () => {
        setIsConfirmingPurchase(false);
      },
    };

    if (cartCost === 0) {
      upsertPet({ userId: currentUser.id, pet: nextPet }, callbacks);
      return;
    }
    purchasePetItem(
      {
        userId: currentUser.id,
        pet: nextPet,
        amount: cartCost,
        description: `Unlocked ${cartItems.map((item) => item.description).join(", ")}`,
      },
      callbacks,
    );
  };

  const handlePurchaseCustom = () => {
    if (!canPurchaseCustom) return;
    // Commissions are repeatable and fulfilled by hand, so nothing lands in
    // unlocked — the spend and its ledger row are the whole purchase.
    purchasePetItem(
      {
        userId: currentUser.id,
        pet: currentUserPet,
        amount: selectedCustom.price,
        description: `Purchased ${selectedCustom.title}`,
      },
      {
        onSuccess: () => {
          setPurchasedCustomIds((current) =>
            new Set(current).add(selectedCustom.id),
          );
          setCelebrationMessage("Purchased! Notify Tyler");
        },
      },
    );
  };

  const handleSubmit = () => {
    if (mode === "CUSTOMIZE") handleSaveOutfit();
    if (mode === "CUSTOM") handlePurchaseCustom();
  };

  /* Render */
  const renderEvolutionButton = () => {
    if (evolutionId === null) return null;

    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outlineDark"
              onClick={() => {
                setIsEvolvedPreview((current) => !current);
              }}
              className={cn(
                "absolute right-1.5 top-1.5 z-20 h-8 w-8 rounded-none border-2 border-slate-900 p-0 shadow-[2px_2px_0_#172033]",
                isEvolvedPreview
                  ? "bg-violet-300 hover:bg-violet-400"
                  : "bg-violet-100 hover:bg-violet-200",
              )}
            >
              <SparklesIcon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="rounded-none border-2 border-slate-900 bg-[#fffaf0] font-mono text-[10px] font-black uppercase text-slate-900">
            {renderEvolutionTooltip()}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderCrownButton = () => (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outlineDark"
            onClick={() => {
              setIsCrownPreview((current) => !current);
            }}
            className={cn(
              "absolute left-1.5 top-1.5 z-20 h-8 w-8 rounded-none border-2 border-slate-900 p-0 shadow-[2px_2px_0_#172033]",
              isCrownPreview
                ? "bg-amber-300 hover:bg-amber-400"
                : "bg-amber-100 hover:bg-amber-200",
            )}
          >
            <CrownIcon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent className="rounded-none border-2 border-slate-900 bg-[#fffaf0] font-mono text-[10px] font-black uppercase text-slate-900">
          {isCrownPreview ? "Take off crown" : "Position your crown"}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const renderAdjustControls = () => {
    if (isCrownPreview) {
      return (
        <ShopAdjustControls
          itemLabel="crown"
          leadingControl={
            <ShopCrownToggle
              isLeaderboardOnly={isCrownLeaderboardOnly}
              onLeaderboardOnlyChange={setIsCrownLeaderboardOnly}
            />
          }
          onScaleChange={handleTopperScaleChange}
        />
      );
    }
    if (activeSlot === "TOPPER" && topperId !== null) {
      return (
        <ShopAdjustControls
          itemLabel={stafferPets.getTopper(topperId)?.label ?? "topper"}
          onScaleChange={handleTopperScaleChange}
        />
      );
    }
    if (isAccessorySlot && activeAccessoryId !== null) {
      return (
        <ShopAdjustControls
          itemLabel={
            stafferPets.getAccessory(activeAccessoryId)?.label ?? "accessory"
          }
          onRotate={handleAccessoryRotate}
          onScaleChange={handleAccessoryScaleChange}
        />
      );
    }
    return null;
  };

  const renderCustomizeContent = () => {
    if (draft === null || collectionStats === null) return null;

    return (
      <Row className="min-h-0 flex-1 gap-5">
        <ShopCharacterSheet
          activeSlot={activeSlot}
          adjustControls={renderAdjustControls()}
          isResetDisabled={!hasOutfitChanges}
          isSaveDisabled={!canSaveOutfit}
          saveLabel={renderSheetSaveLabel()}
          preview={
            <>
              <Column
                className={cn(
                  "items-center justify-center",
                  previewSpecies === "BALD_EAGLE" && "translate-y-3",
                  celebrationMessage && "staffer-pet-bounce-triple",
                )}
              >
                <StafferPetSprite
                  species={previewSpecies}
                  paletteId={draft.colorId}
                  sizePx={stafferPets.getDisplaySizePx(
                    previewSpecies,
                    PET_PREVIEW_SIZE_PX,
                  )}
                  isEvolved={isPreviewEvolved}
                  topperId={previewTopperId}
                  topperOffset={previewTopperOffset}
                  onTopperOffsetChange={
                    activeSlot === "TOPPER" || isCrownPreview
                      ? handleTopperOffsetChange
                      : undefined
                  }
                  accessoryId={accessoryId}
                  accessoryAdjustment={previewAccessoryAdjustment}
                  accessoryId2={accessoryId2}
                  accessoryAdjustment2={previewAccessoryAdjustment2}
                  onAccessoryAdjustmentChange={
                    isAccessorySlot
                      ? (adjustment) => {
                          handleAccessoryAdjustmentChange(1, adjustment);
                        }
                      : undefined
                  }
                  onAccessoryAdjustmentChange2={
                    isAccessorySlot
                      ? (adjustment) => {
                          handleAccessoryAdjustmentChange(2, adjustment);
                        }
                      : undefined
                  }
                  onAccessoryDragStart={
                    isAccessorySlot ? handleAccessoryDragStart : undefined
                  }
                />
              </Column>
              {celebrationMessage ? (
                <ShopUnlockCelebration message={celebrationMessage} />
              ) : null}
              {renderCrownButton()}
              {renderEvolutionButton()}
            </>
          }
          slots={{
            PET: getSheetSlot("PET"),
            COLOR: getSheetSlot("COLOR"),
            TOPPER: getSheetSlot("TOPPER"),
            ACCESSORY_1: getSheetSlot("ACCESSORY_1"),
            ACCESSORY_2: getSheetSlot("ACCESSORY_2"),
          }}
          stats={collectionStats}
          subtitle={hasOutfitChanges ? "Unsaved changes" : "Saved outfit"}
          title={stafferPets.getSpeciesLabel(previewSpecies)}
          onReset={handleReset}
          onSave={handleSaveOutfit}
          onShuffle={handleShuffle}
          onSlotClear={handleSlotClear}
          onSlotSelect={setActiveSlot}
        />
        <ShopCatalogGrid
          coinBalance={coinBalance}
          description={shopCatalog.slotCopy[activeSlot].description}
          entries={catalogEntries}
          filter={itemFilter}
          title={shopCatalog.slotCopy[activeSlot].title}
          onFilterChange={setItemFilter}
          onSelect={handleItemSelect}
        />
      </Row>
    );
  };

  const renderStandaloneVisual = () => {
    if (mode === "INCENTIVES") return <ShopIncentiveRewards />;
    return (
      <StafferPetProfileSprite
        pet={currentUserPet}
        sizePx={
          currentUserPet
            ? stafferPets.getDisplaySizePx(
                currentUserPet.equipped.petId,
                STANDALONE_PREVIEW_SIZE_PX,
              )
            : STANDALONE_PREVIEW_SIZE_PX
        }
      />
    );
  };

  const renderStandaloneCatalog = () => {
    if (currentUserPet === null) return null;
    if (mode === "INCENTIVES") {
      return <ShopIncentives pet={currentUserPet} userId={currentUser?.id} />;
    }
    return movePurchasedItemsToBottom(customCommissionOptions, (option) =>
      purchasedCustomIds.has(option.id),
    ).map((option) => (
      <CustomCommissionCard
        key={option.id}
        isOwned={purchasedCustomIds.has(option.id)}
        isSelected={selectedCustomId === option.id}
        option={option}
        onSelect={handleCustomSelect}
      />
    ));
  };

  const renderStandaloneContent = () => {
    const section =
      stafferPetShop.sections[mode === "CUSTOMIZE" ? "CUSTOM" : mode];

    return (
      <Row className="min-h-0 flex-1 gap-5">
        <Column className="w-[420px] shrink-0 items-center justify-center gap-3 border-4 border-slate-900 bg-[#dff3ff] p-5 shadow-[6px_6px_0_#172033]">
          <Column className="relative h-64 w-64 items-center justify-center overflow-hidden border-4 border-slate-900 bg-white p-3 shadow-[5px_5px_0_#172033]">
            <Column
              className={cn(
                "items-center justify-center",
                celebrationMessage && "staffer-pet-bounce-triple",
              )}
            >
              {renderStandaloneVisual()}
            </Column>
            {celebrationMessage ? (
              <ShopUnlockCelebration message={celebrationMessage} />
            ) : null}
          </Column>
          <Text className="text-center font-mono text-lg font-black uppercase tracking-[0.2em] text-slate-900">
            {renderStandaloneLabel()}
          </Text>
          <Text className="font-mono text-[10px] font-black uppercase tracking-[0.14em] text-orange-600">
            {renderStandaloneMeta()}
          </Text>
        </Column>

        <Column className="min-w-0 flex-1 gap-3">
          <Column className="gap-1 border-b-4 border-slate-900 pb-3">
            <Text className="font-mono text-sm font-black uppercase tracking-[0.14em] text-slate-900">
              {section.title}
            </Text>
            <Text className="font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">
              {section.description}
            </Text>
          </Column>
          <Column className="gap-3 overflow-y-auto pr-2">
            {renderStandaloneCatalog()}
          </Column>
        </Column>
      </Row>
    );
  };

  const renderShopContent = () => {
    if (currentUserPet === null) {
      return (
        <Column className="min-h-0 flex-1 items-center justify-center gap-4 border-4 border-slate-900 bg-orange-50 p-8 shadow-[6px_6px_0_#172033]">
          <ShoppingBagIcon className="h-12 w-12 text-orange-500" />
          <Text className="text-center font-mono text-sm font-black uppercase tracking-[0.12em] text-slate-900">
            Adopt a pet before visiting the shop
          </Text>
        </Column>
      );
    }
    if (mode === "CUSTOMIZE") return renderCustomizeContent();
    return renderStandaloneContent();
  };

  const render = () => {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-pet-shop=""
          hideCloseButton
          className="staffer-pet-pixel-modal grid h-[min(92vh,780px)] w-[min(96vw,1180px)] max-w-none grid-rows-[auto_minmax(0,1fr)_auto] gap-4 rounded-none border-4 border-slate-900 bg-[#fffaf0] px-6 pb-6 pt-4 sm:rounded-none"
        >
          <DialogHeader className="border-b-4 border-slate-900 pb-3 text-left">
            <Row className="w-full flex-wrap items-center justify-between gap-4">
              <Column className="gap-0">
                <DialogTitle className="font-mono text-xl font-black uppercase tracking-[0.1em] text-slate-900">
                  Pet Shop
                </DialogTitle>
                <DialogDescription className="font-mono text-xs uppercase tracking-[0.18em] text-orange-600">
                  Customize your pet · spend coins · unlock treasures
                </DialogDescription>
              </Column>
              <Row className="items-center gap-3">
                <ShopModeTabs
                  modes={shopModes}
                  selectedMode={mode}
                  onSelect={handleModeSelect}
                />
                <Row className="items-center gap-2 border-2 border-slate-900 bg-amber-300 px-3 py-2 shadow-[3px_3px_0_#172033]">
                  <CoinsIcon className="h-4 w-4 text-amber-900" />
                  <Text className="font-mono text-sm font-black text-slate-900">
                    {coinBalance}
                  </Text>
                  <Text className="font-mono text-[9px] font-black uppercase tracking-[0.12em] text-amber-900">
                    Coins
                  </Text>
                </Row>
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <CircleHelpIcon className="h-4 w-4 text-slate-500 hover:text-slate-900" />
                    </TooltipTrigger>
                    <TooltipContent
                      align="end"
                      side="bottom"
                      className="w-64 rounded-none border-2 border-slate-900 bg-[#fffaf0] p-3 shadow-[3px_3px_0_#172033]"
                    >
                      <Column className="gap-2">
                        <Text className="font-mono text-[10px] font-black uppercase tracking-[0.1em] text-slate-900">
                          How to earn coins
                        </Text>
                        {[
                          ...Object.values(STAFFER_PET_REWARD_DETAILS),
                          STAFFER_PET_SEARCH_SUBMIT_REWARD,
                        ].map((reward) => (
                          <Row
                            key={reward.label}
                            className="items-center justify-between gap-4"
                          >
                            <Text className="font-mono text-[9px] font-bold uppercase tracking-[0.06em] text-slate-600">
                              {reward.label}
                            </Text>
                            <Text className="font-mono text-[10px] font-black text-emerald-700">
                              +{reward.amount.toString()}
                            </Text>
                          </Row>
                        ))}
                      </Column>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <DialogClose asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-8 w-8 shrink-0 p-0 text-slate-900 hover:bg-orange-100"
                  >
                    <XIcon />
                    <Text className="sr-only">Close</Text>
                  </Button>
                </DialogClose>
              </Row>
            </Row>
          </DialogHeader>

          {renderShopContent()}

          <Row className="items-center gap-3 border-t-4 border-slate-900 pt-4">
            <Button
              type="button"
              variant="outlineDark"
              onClick={onBack}
              className="h-12 min-w-28 border-2 border-slate-900 bg-white px-4 font-mono text-xs font-black uppercase tracking-[0.1em] text-slate-900 shadow-[3px_3px_0_#172033] hover:bg-orange-50"
            >
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              Yard
            </Button>
            <Row className="min-w-0 flex-1">
              {mode === "CUSTOMIZE" ? (
                <ShopOutfitChanges
                  changes={getOutfitChanges()}
                  isPositionDirty={isPositionDirty}
                />
              ) : null}
            </Row>
            <Button
              type="button"
              variant="outlineDark"
              disabled={!canSubmitByMode[mode]}
              onClick={handleSubmit}
              className={cn(
                "h-12 min-w-[300px] border-2 border-slate-900 bg-slate-900 px-8 font-mono text-sm font-black uppercase tracking-[0.12em] text-orange-50 shadow-[3px_3px_0_#f97316] hover:bg-slate-800 disabled:opacity-60",
                isConfirmingPurchase &&
                  "bg-orange-500 shadow-[3px_3px_0_#172033] hover:bg-orange-600",
              )}
            >
              <ShoppingBagIcon className="mr-2 h-4 w-4" />
              {renderSubmitLabel()}
            </Button>
          </Row>
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};

type ShopIncentive = (typeof stafferPetShop.sections.INCENTIVES.items)[number];

// Icon + color live on the config item (stafferPetShop.ts); only the icon
// key → lucide component mapping lives here.
const incentiveIcons: Record<ShopIncentive["icon"], LucideIcon> = {
  FOOD: UtensilsIcon,
  MASSAGE: HandHeartIcon,
  WORKOUT: DumbbellIcon,
};

const ShopIncentiveRewards = () => {
  /* Render */
  const render = () => {
    return (
      <Column className="items-center gap-4">
        <StafferPetIncentiveToken sizePx={88} />
        <Row className="items-end justify-center gap-3">
          {stafferPetShop.sections.INCENTIVES.items.map((incentive, index) => {
            const Icon = incentiveIcons[incentive.icon];
            const isOdd = index % 2 === 1;
            return (
              <Column
                key={incentive.id}
                className={cn(
                  "h-16 w-16 items-center justify-center border-4 border-slate-900 shadow-[4px_4px_0_#172033]",
                  isOdd ? "translate-y-2 rotate-6" : "-rotate-6",
                  incentive.colorClassName,
                )}
              >
                <Icon className="h-8 w-8 text-slate-900" />
              </Column>
            );
          })}
        </Row>
      </Column>
    );
  };

  return render();
};

const ShopIncentives = ({
  pet,
  userId,
}: {
  pet: Pet;
  userId: number | undefined;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: transactions } = useGetPetTransactions(userId);
  // Update
  const { mutate: redeemIncentive, isPending: isRedeeming } =
    useRedeemIncentive();

  /* React Hooks */
  // A human fulfills redemptions, so the button asks for a second click.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  /* Helpers */
  const tokensAvailable = pet.incentiveTokensAvailable;
  // Display-only progress; the server owns the real token math.
  const coinsIntoNextToken =
    pet.totalCoinsEarned % stafferPetShop.coinsPerIncentiveToken;
  const progressPercent =
    (coinsIntoNextToken / stafferPetShop.coinsPerIncentiveToken) * 100;
  const redemptions = (transactions ?? []).filter(
    (transaction) => transaction.type === "INCENTIVE_REDEMPTION",
  );
  const canRedeem = userId !== undefined && tokensAvailable > 0 && !isRedeeming;

  /* Handlers */
  const handleRedeemClick = (incentive: ShopIncentive) => {
    if (confirmingId !== incentive.id) {
      setConfirmingId(incentive.id);
      return;
    }
    if (userId === undefined) return;
    // The title lands on the ledger row so whoever fulfills it knows what to buy.
    redeemIncentive(
      { userId, description: incentive.title },
      {
        onSettled: () => {
          setConfirmingId(null);
        },
      },
    );
  };

  /* Render */
  const renderRedeemLabel = (incentive: ShopIncentive) => {
    if (isRedeeming && confirmingId === incentive.id) return "Redeeming…";
    if (confirmingId === incentive.id) return "Click again to confirm";
    if (tokensAvailable === 0) return "No tokens yet";
    return (
      <>
        Redeem · {stafferPetShop.incentiveTokenCost.toString()}
        <StafferPetIncentiveToken sizePx={16} />
      </>
    );
  };

  const renderTokenHeader = () => {
    return (
      <Column className="gap-3 border-2 border-slate-900 bg-violet-50 p-4 shadow-[3px_3px_0_#172033]">
        <Row className="items-center gap-3">
          <StafferPetIncentiveToken sizePx={44} />
          <Column className="min-w-0 flex-1 gap-0">
            <Text className="font-mono text-xl font-black text-slate-900">
              {tokensAvailable.toString()} incentive token
              {tokensAvailable === 1 ? "" : "s"}
            </Text>
            <Text className="font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-slate-500">
              {pet.incentiveTokensRedeemed.toString()} redeemed ·{" "}
              {pet.incentiveTokensEarned.toString()} earned all-time
            </Text>
          </Column>
        </Row>
        <Column className="gap-1">
          <Column className="h-3 w-full overflow-hidden border-2 border-slate-900 bg-white">
            <Column
              className="h-full bg-violet-500"
              style={{ width: `${progressPercent.toString()}%` }}
            />
          </Column>
          <Text className="font-mono text-[9px] font-black uppercase tracking-[0.08em] text-violet-700">
            {coinsIntoNextToken.toLocaleString("en-US")} /{" "}
            {stafferPetShop.coinsPerIncentiveToken.toLocaleString("en-US")}{" "}
            coins to next token
          </Text>
        </Column>
        <Text className="font-mono text-[8px] font-bold uppercase leading-3 tracking-[0.08em] text-slate-500">
          Every {stafferPetShop.coinsPerIncentiveToken.toString()} coins you
          earn = 1 incentive token. Spending coins never slows this down.
        </Text>
      </Column>
    );
  };

  const renderIncentiveCard = (incentive: ShopIncentive) => {
    const Icon = incentiveIcons[incentive.icon];

    return (
      <Row
        key={incentive.id}
        className="items-center gap-3 border-2 border-slate-900 bg-white p-3 shadow-[3px_3px_0_#172033]"
      >
        <Column
          className={cn(
            "h-12 w-12 shrink-0 items-center justify-center border-2 border-slate-900",
            incentive.colorClassName,
          )}
        >
          <Icon className="h-6 w-6 text-slate-900" />
        </Column>
        <Column className="min-w-0 flex-1 gap-1">
          <Text className="font-mono text-[11px] font-black uppercase tracking-[0.08em] text-slate-900">
            {incentive.title}
          </Text>
          <Text className="font-mono text-[9px] font-bold uppercase leading-4 tracking-[0.05em] text-slate-500">
            {incentive.description}
          </Text>
        </Column>
        <Button
          type="button"
          variant="outlineDark"
          disabled={!canRedeem}
          onClick={() => {
            handleRedeemClick(incentive);
          }}
          className={cn(
            "h-9 shrink-0 gap-1.5 rounded-none border-2 border-slate-900 bg-violet-100 px-3 font-mono text-[10px] font-black uppercase tracking-[0.06em] text-slate-900 shadow-[2px_2px_0_#172033] hover:bg-violet-200 disabled:opacity-60",
            confirmingId === incentive.id &&
              "bg-orange-200 hover:bg-orange-300",
          )}
        >
          {renderRedeemLabel(incentive)}
        </Button>
      </Row>
    );
  };

  const renderRedeemedHistory = () => {
    if (redemptions.length === 0) return null;

    return (
      <Column className="gap-2 border-2 border-slate-900 bg-white p-3 shadow-[3px_3px_0_#172033]">
        <Text className="font-mono text-[10px] font-black uppercase tracking-[0.12em] text-slate-900">
          Redeemed
        </Text>
        {redemptions.map((redemption) => (
          <Row
            key={redemption.id}
            className="items-center justify-between gap-3 border-b-2 border-dashed border-slate-200 pb-2 last:border-b-0 last:pb-0"
          >
            <Row className="min-w-0 items-center gap-2">
              <StafferPetIncentiveToken sizePx={18} />
              <Text className="truncate font-mono text-[10px] font-black uppercase tracking-[0.06em] text-slate-900">
                {redemption.description}
              </Text>
            </Row>
            <Text className="shrink-0 font-mono text-[9px] font-bold uppercase tracking-[0.08em] text-slate-500">
              {dates.formatShortDateTime(redemption.createdAt)}
            </Text>
          </Row>
        ))}
      </Column>
    );
  };

  const render = () => {
    return (
      <>
        {renderTokenHeader()}
        {stafferPetShop.sections.INCENTIVES.items.map(renderIncentiveCard)}
        {renderRedeemedHistory()}
      </>
    );
  };

  return render();
};

const ShopUnlockCelebration = ({ message }: { message: string }) => {
  /* Render */
  const render = () => {
    return (
      <Column className="pointer-events-none absolute inset-0 z-10 items-center justify-center">
        <Column className="staffer-pet-shop-sparkle absolute left-5 top-7 h-3 w-3 bg-amber-400" />
        <Column className="staffer-pet-shop-sparkle absolute right-7 top-12 h-4 w-4 bg-violet-500 [animation-delay:120ms]" />
        <Column className="staffer-pet-shop-sparkle absolute bottom-12 left-9 h-4 w-4 bg-orange-500 [animation-delay:240ms]" />
        <Column className="staffer-pet-shop-sparkle absolute bottom-7 right-12 h-3 w-3 bg-emerald-400 [animation-delay:360ms]" />
        <Text className="staffer-pet-shop-unlock-banner absolute bottom-4 border-2 border-slate-900 bg-amber-300 px-3 py-1 font-mono text-xs font-black uppercase tracking-[0.14em] text-slate-900 shadow-[3px_3px_0_#172033]">
          {message}
        </Text>
      </Column>
    );
  };

  return render();
};

const CustomCommissionCard = ({
  isOwned,
  isSelected,
  option,
  onSelect,
}: {
  isOwned: boolean;
  isSelected: boolean;
  option: CustomCommission;
  onSelect: (customId: CustomCommissionId) => void;
}) => {
  /* Helpers */
  const renderIcon = () => {
    if (option.icon === "COLOR") return <PaletteIcon className="h-7 w-7" />;
    if (option.icon === "ACCESSORY") return <SwordIcon className="h-7 w-7" />;
    if (option.icon === "EVOLUTION")
      return <SparklesIcon className="h-7 w-7" />;
    if (option.icon === "LEADERBOARD")
      return <TrophyIcon className="h-7 w-7" />;
    return <PawPrintIcon className="h-7 w-7" />;
  };

  const renderStatus = () => {
    if (isOwned) {
      return (
        <Row className="items-center gap-1 font-mono text-[10px] font-black uppercase text-emerald-700">
          <CheckIcon className="h-4 w-4" />
          Purchased
        </Row>
      );
    }
    return (
      <Row className="items-center gap-1 border-2 border-slate-900 bg-amber-300 px-2 py-1">
        <LockIcon className="h-3 w-3" />
        <Text className="font-mono text-xs font-black text-slate-900">
          {option.price === null
            ? "PRICE TBD"
            : stafferPetShop.getPriceLabel(option.price)}
        </Text>
      </Row>
    );
  };

  /* Render */
  const render = () => {
    return (
      <Button
        type="button"
        variant="outlineDark"
        onClick={() => {
          onSelect(option.id);
        }}
        className={cn(
          "h-auto w-full flex-col items-stretch gap-3 rounded-none border-2 border-slate-900 bg-white p-4 text-slate-900 shadow-[3px_3px_0_#172033] hover:bg-orange-50",
          isSelected &&
            "translate-x-1 bg-orange-100 shadow-[1px_1px_0_#172033]",
        )}
      >
        <Row className="min-w-0 items-center gap-3">
          <Column className="h-12 w-12 shrink-0 items-center justify-center border-2 border-slate-900 bg-violet-100 text-violet-700">
            {renderIcon()}
          </Column>
          <Column className="min-w-0 flex-1 items-start gap-0 text-left">
            <Text className="w-full break-words font-mono text-xs font-black uppercase tracking-[0.1em] text-slate-900 [overflow-wrap:anywhere]">
              {option.title}
            </Text>
            <Text className="w-full whitespace-normal break-words font-mono text-[9px] font-bold uppercase leading-4 tracking-[0.06em] text-slate-500 [overflow-wrap:anywhere]">
              {option.description}
            </Text>
          </Column>
          {renderStatus()}
        </Row>
        <Row
          className={cn(
            "items-center justify-center border-2 border-dashed px-3 py-2",
            isOwned
              ? "border-emerald-500 bg-emerald-50"
              : "border-violet-400 bg-violet-50",
          )}
        >
          <Text className="font-mono text-[9px] font-black uppercase tracking-[0.12em] text-violet-700">
            {isOwned
              ? "Notify Tyler to start your commission"
              : "One-of-one commission"}
          </Text>
        </Row>
      </Button>
    );
  };

  return render();
};
